import { useDisclosure } from '@mantine/hooks';
import clsx from 'clsx';
import { useTranslation } from 'next-i18next/pages';

import { Stack, UnstyledButton } from '@highlight-cp/core-ui';

import type { AiChatMessagePart, Role } from '../ai-chat-types';
import { AssetGalleryModal } from '../asset-gallery';
import { ImageWithLoader } from '../image-with-loader';
import classes from './ai-chat-window-image.module.css';
import { getImageSrcFromPart } from './utils/get-image-src-from-part';

interface AiChatWindowImageProps {
  part: AiChatMessagePart;
  role: Role;
}

const IMAGE_WIDTH = 240;
const IMAGE_HEIGHT = 180;

/**
 * ### AiChatWindowImage
 *
 * @description
 * An image attached to a chat message, shown as a thumbnail. Clicking it opens
 * the image full size in a zoomable lightbox.
 *
 * @param props - The image message part and the role of the message it's in
 * @returns The thumbnail and its lightbox, or nothing when the part has no image
 */
const AiChatWindowImage = ({ part, role }: AiChatWindowImageProps) => {
  const { t } = useTranslation();
  const [isLightboxOpen, { open, close }] = useDisclosure(false);
  const src = getImageSrcFromPart(part);

  if (!src) {
    return null;
  }

  const alt = part.filename ?? t('Attached image');

  return (
    <Stack className={clsx(classes.attachment, classes[role])} gap="xs">
      <UnstyledButton
        aria-haspopup="dialog"
        aria-label={t('Enlarge {{name}}', { name: alt })}
        className={classes.trigger}
        onClick={open}
      >
        <ImageWithLoader
          src={src}
          alt={alt}
          isLoading={false}
          width={IMAGE_WIDTH}
          height={IMAGE_HEIGHT}
          radius="md"
          fit="contain"
        />
      </UnstyledButton>
      <AssetGalleryModal
        assets={[{ url: src, alt, name: part.filename }]}
        opened={isLightboxOpen}
        onClose={close}
      />
    </Stack>
  );
};

export default AiChatWindowImage;
